import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { CodeInput } from "@/components/shared/CodeInput";
import { isTotpStepUpRequiredError, stepUpTotp } from "@/services/totpService";

/**
 * Wraps a sensitive action so a step-up challenge is answered IN PLACE.
 *
 * The backend refuses a bank-account change or a payout request with
 * `totp_step_up_required` when the session's last verification is older than
 * the grace window. The client could redirect to a code screen — the
 * enrollment path does — but here that would discard whatever the professional
 * had typed into the form. So this asks for the code over the top, verifies,
 * and then RE-RUNS the action.
 *
 * Usage:
 *
 *   const { guard, dialog } = useTotpStepUp();
 *   await guard(() => requestProfessionalPayout());
 *   // ...and render {dialog} once in the component
 *
 * `guard` is transparent on the happy path: no challenge, no dialog, the
 * action's own result and its own errors. Only a step-up refusal is
 * intercepted, and only ONE retry is attempted — a loop here would re-prompt
 * forever against a server that keeps refusing for some other reason.
 *
 * Spec 5d9018f9, control C-8.
 */
export function useTotpStepUp(): {
  guard: <T>(action: () => Promise<T>) => Promise<T>;
  dialog: JSX.Element;
} {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  /**
   * Resolves when the dialog closes: true when a code was accepted.
   *
   * A ref rather than state because `guard` awaits it inside a closure that
   * must not re-render to see the answer.
   */
  const pending = useRef<((verified: boolean) => void) | null>(null);

  const settle = useCallback((verified: boolean): void => {
    const resolve = pending.current;
    pending.current = null;
    setOpen(false);
    setCode("");
    if (resolve) resolve(verified);
  }, []);

  const guard = useCallback(
    async <T,>(action: () => Promise<T>): Promise<T> => {
      try {
        return await action();
      } catch (error: unknown) {
        if (!isTotpStepUpRequiredError(error)) {
          throw error;
        }
        const verified = await new Promise<boolean>((resolve) => {
          pending.current = resolve;
          setOpen(true);
        });
        if (!verified) {
          // Cancelled. Rethrow the ORIGINAL refusal so the caller's own error
          // handling runs — swallowing it would leave the UI looking like the
          // action succeeded.
          throw error;
        }
        return action();
      }
    },
    []
  );

  const submit = async (): Promise<void> => {
    const digits = code.replace(/\D/g, "").slice(0, 6);
    if (digits.length !== 6) {
      toast.error(t("security.twoFactor.codeInvalid"));
      return;
    }
    setBusy(true);
    try {
      await stepUpTotp(digits);
      settle(true);
    } catch {
      // One generic message: this is a verification attempt, and naming the
      // reason would distinguish a wrong code from a replayed one.
      toast.error(t("security.twoFactor.stepUpInvalid"));
    } finally {
      setBusy(false);
    }
  };

  const dialog = (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing by overlay or Escape counts as cancelling, so the pending
        // promise must settle — otherwise the awaiting action hangs forever.
        if (!next && !busy) settle(false);
      }}
    >
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" aria-hidden />
            {t("security.twoFactor.stepUpTitle")}
          </DialogTitle>
          <DialogDescription>{t("security.twoFactor.stepUpBody")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          <Label htmlFor="totp-step-up-code">{t("security.twoFactor.codeLabel")}</Label>
          <CodeInput
            id="totp-step-up-code"
            autoFocus
            value={code}
            onChange={setCode}
            onComplete={() => void submit()}
            disabled={busy}
          />
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => settle(false)}
          >
            {t("security.twoFactor.stepUpCancel")}
          </Button>
          <Button
            disabled={busy || code.replace(/\D/g, "").length !== 6}
            onClick={() => void submit()}
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                {t("security.twoFactor.stepUpConfirming")}
              </>
            ) : (
              t("security.twoFactor.stepUpConfirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return { guard, dialog };
}
